import React from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import fdbService from '../../../../../services/fdbService';
import PatientEdPrintDialog from '../../../../../components/counseling/PatientEdPrintDialog';
import { apiErrorMessage } from '../../../../../utils/apiErrorMessage';

export interface RxInfoSheetDialogProps {
  open: boolean;
  onClose: () => void;
  din: string;
  drugName: string;
}

/**
 * PRX-137 — "Info sheet" from the Prescriptions row menu: the FDB patient
 * education the counseling and product verification flows already print,
 * through the same `PatientEdPrintDialog`. This is print only. Sending it to
 * the patient waits on PRX-13 / D7.
 */
export const RxInfoSheetDialog: React.FC<RxInfoSheetDialogProps> = ({ open, onClose, din, drugName }) => {
  const query = useQuery({
    queryKey: ['fdb-patient-ed', din],
    queryFn: () => fdbService.getPatientEducation(din),
    enabled: open && !!din,
  });

  if (!open) return null;

  if (query.data && query.data.monographs?.length > 0) {
    return (
      <PatientEdPrintDialog
        open
        onClose={onClose}
        drugName={drugName}
        din={din}
        monographs={query.data.monographs}
        counselingMessages={[]}
      />
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Info sheet: {drugName}</DialogTitle>
      <DialogContent>
        {query.isError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => query.refetch()}>
                Retry
              </Button>
            }
          >
            Couldn't load the info sheet. {apiErrorMessage(query.error, '')}
          </Alert>
        ) : query.data ? (
          <Alert severity="info">FDB has no patient information sheet for this drug.</Alert>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} aria-label="Loading the info sheet" />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RxInfoSheetDialog;
